/**
 * DashboardV2Page — Home V2 · Executive Dashboard Premium (v2.3)
 *
 * Ruta: /home (la raíz «/» y la antigua «/home-v2» redirigen aquí).
 *
 * Layout desktop:
 *  1. Greeting Header  — saludo · timestamp · refresh
 *  2. Hero Banner      — hero.png panorámico · tagline · badge estado sólido
 *  3. KPI Strip        — 4 tarjetas: prodRiesgo / matRiesgo / otsFecha / cobertura
 *  4. Analytical Block — gráfico semanal (SVG, 3 col) + tabla riesgo (2 col, h igual)
 *  5. Alerts + Quick   — alertas accionables (3 col) + acceso rápido con subtítulos (2 col)
 *
 * Layout mobile:
 *  1. Greeting Header compacto
 *  2. Mobile Alert Callout (si hay alertas)
 *  3. Grid 2×2 KPIs
 *  4. Gráfico compacto
 *  5. Risk list (max 5 items)
 *  6. Alertas y focos
 *  7. Acceso rápido — scroll horizontal con snap
 */
import type { ReactNode } from 'react'
import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMsal } from '@azure/msal-react'
import { useQuery } from '@tanstack/react-query'
import { useAppStore } from '@/store'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { useSinProduccion } from '@/hooks/useSinProduccion'
import { useHasReportsAccess } from '@/hooks/useHasReportsAccess'
import { dashboardApi } from '@/services/api'
import { useBrand } from '@/branding/useBrand'
import type { Brand } from '@/branding/types'
import type { ProduccionRiesgoItem, ProduccionSemanalPoint } from '@/types'

type TFunc = (key: string, opts?: Record<string, unknown>) => string
type StatusKind = 'ok' | 'warn' | 'danger' | 'neutral'
type AccentColor = 'emerald' | 'amber' | 'rose' | 'sky' | 'neutral'

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtN(n: number | null | undefined): string {
  if (n == null) return '0'
  return n.toLocaleString('es-ES', { maximumFractionDigits: 0 })
}
/** Unidades siempre con el número completo (10.500, no «10,5 k»). */
function fmtUds(n: number | null | undefined): string {
  return fmtN(n ?? 0)
}
function fmtPct(n: number | null | undefined): string {
  const v = n ?? 0
  return `${v.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`
}
function fmtDate(iso: string | undefined): string {
  if (!iso) return '—'
  try {
    const [y, m, d] = iso.split('-')
    return `${d}/${m}/${y.slice(2)}`
  } catch {
    return iso
  }
}
function daysOverdue(iso: string | undefined): number {
  if (!iso) return 0
  const diff = Date.now() - new Date(iso).getTime()
  return Math.max(0, Math.floor(diff / 86_400_000))
}

// ── SVG Icons 20×20 ───────────────────────────────────────────────────────────

const IcoAlert = () => (
  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2.5L2 17.5h16L10 2.5z"/>
    <path d="M10 8.5v4.5M10 15.5h.01"/>
  </svg>
)
const IcoBox = () => (
  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 5l8-2.5L18 5v10l-8 2.5L2 15V5z"/>
    <path d="M10 2.5V17.5M2 5l8 2.5 8-2.5"/>
  </svg>
)
const IcoClock = () => (
  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="8"/>
    <path d="M10 5.5v5l3 2"/>
  </svg>
)
const IcoShield = () => (
  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2L3 5v5c0 4.4 3 7.3 7 8.5 4-1.2 7-4.1 7-8.5V5L10 2z"/>
    <path d="M7 10l2 2 4-4"/>
  </svg>
)
const IcoRefresh = ({ spin }: { spin: boolean }) => (
  <svg className={`w-4 h-4 shrink-0 ${spin ? 'animate-spin' : ''}`}
    viewBox="0 0 16 16" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 8A6.5 6.5 0 0113.5 4M14.5 8A6.5 6.5 0 012.5 12"/>
    <path d="M13.5 1v3h-3M2.5 15v-3h3"/>
  </svg>
)
const IcoArrowSm = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 14 14" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.5 7h9M8 3.5L11.5 7 8 10.5"/>
  </svg>
)
const IcoChevronRight = () => (
  <svg className="w-3 h-3 shrink-0" viewBox="0 0 12 12" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10l4-4-4-4"/>
  </svg>
)

// Quick access icons 24×24
const IcoCart24 = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 5h18l-2 9H5L3 5z"/>
    <path d="M8 5V4a2 2 0 012-2h4a2 2 0 012 2v1"/>
    <circle cx="9" cy="19" r="1.5"/>
    <circle cx="17" cy="19" r="1.5"/>
  </svg>
)
const IcoBag24 = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5h16l-2 13H6L4 5z"/>
    <path d="M9 10h6M10 14h4"/>
    <path d="M15 5V4a2 2 0 00-2-2h-2a2 2 0 00-2 2v1"/>
  </svg>
)
const IcoBars24 = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="14" width="5" height="7" rx="0.8"/>
    <rect x="9.5" y="8" width="5" height="13" rx="0.8"/>
    <rect x="16" y="3" width="5" height="18" rx="0.8"/>
    <polyline points="3,11 8,6 13,9 20,2"/>
  </svg>
)
const IcoBox24 = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 7l10-4 10 4v10l-10 4L2 17V7z"/>
    <path d="M12 3v18M2 7l10 4 10-4"/>
  </svg>
)
// Materia prima: un bidón de granel.
const IcoDrum24 = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="7" ry="2.6"/>
    <path d="M5 5v14c0 1.44 3.13 2.6 7 2.6s7-1.16 7-2.6V5"/>
    <path d="M5 10.5c0 1.44 3.13 2.6 7 2.6s7-1.16 7-2.6"/>
    <path d="M5 15c0 1.44 3.13 2.6 7 2.6s7-1.16 7-2.6"/>
  </svg>
)
// Producto terminado: el frasco.
const IcoBottle24 = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.5 2.5h5v3h-5z"/>
    <path d="M10 5.5v2.2c0 .5-.24.96-.65 1.24A5 5 0 007 13.1V19a2.5 2.5 0 002.5 2.5h5A2.5 2.5 0 0017 19v-5.9a5 5 0 00-2.35-4.16c-.41-.28-.65-.74-.65-1.24V5.5"/>
    <path d="M7 14.5h10"/>
  </svg>
)
// Cobertura: componentes que confluyen en un terminado.
const IcoNet24 = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="4.5" cy="5" r="2"/>
    <circle cx="4.5" cy="19" r="2"/>
    <circle cx="19.5" cy="12" r="2"/>
    <path d="M6.5 5.6q6 1.4 11 5.7M6.5 18.4q6-1.4 11-5.7"/>
    <path d="M4.5 7v10"/>
  </svg>
)
const IcoCal24 = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round">
    <rect x="3" y="4" width="18" height="18" rx="2.5"/>
    <path d="M3 9h18M8 4v5M16 4v5"/>
    <path d="M7 13h2M11 13h2M15 13h2M7 17h2M11 17h2M15 17h2"/>
  </svg>
)

const IcoTruck24 = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7h10v9H3z"/>
    <path d="M13 10h4l3 3v3h-7z"/>
    <circle cx="7" cy="18" r="1.8"/>
    <circle cx="17" cy="18" r="1.8"/>
  </svg>
)

// ── 1. Greeting Header ────────────────────────────────────────────────────────

interface GreetingHeaderProps {
  dark: boolean
  firstName: string
  updatedAgo: string
  isFetching: boolean
  onRefresh: () => void
  t: TFunc
}

function GreetingHeader({ dark, firstName, updatedAgo, isFetching, onRefresh, t }: GreetingHeaderProps) {
  const hour = new Date().getHours()
  const greetKey = hour < 12 ? 'homeV2.greetMorning' : hour < 19 ? 'homeV2.greetAfternoon' : 'homeV2.greetEvening'
  const noNameKey = hour < 12 ? 'homeV2.greetMorningNoName' : hour < 19 ? 'homeV2.greetAfternoonNoName' : 'homeV2.greetEveningNoName'
  const greetName = firstName ? t(greetKey, { name: firstName }) : t(noNameKey)

  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h1 className={`text-[1.3rem] sm:text-[1.5rem] font-bold tracking-tight leading-tight
          ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
          {greetName}
        </h1>
        <p className={`text-[12px] mt-0.5 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
          {t('homeV2.headerSubtitle')}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {updatedAgo && (
          <span className={`text-[10.5px] hidden sm:inline ${dark ? 'text-slate-600' : 'text-slate-400'}`}>
            {updatedAgo}
          </span>
        )}
        <button
          onClick={onRefresh}
          disabled={isFetching}
          className={`flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg border
            transition-colors cursor-pointer disabled:opacity-40
            ${dark
              ? 'bg-slate-800 border-white/10 text-slate-300 hover:bg-slate-700'
              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 shadow-sm'}`}
        >
          <IcoRefresh spin={isFetching} />
          <span className="hidden sm:inline">{t('homeV2.refresh')}</span>
        </button>
      </div>
    </div>
  )
}

// ── 2. Hero Banner (hidden on mobile) ─────────────────────────────────────────
// FIX v2.3: backgroundPosition ajustado a 'center 38%' para vista panorámica.
// Badge de estado ahora es sólido y legible (sin translucidez).

function HeroBanner({ dark, overallStatus, overallLabel, t, brand }: {
  dark: boolean; overallStatus: StatusKind; overallLabel: string; t: TFunc; brand: Brand
}) {
  // Pastillas sólidas — legibles sobre cualquier zona de la imagen
  const statusPill: Record<StatusKind, string> = {
    ok:      'bg-emerald-600 text-white shadow-md',
    warn:    'bg-amber-500   text-white shadow-md',
    danger:  'bg-rose-600    text-white shadow-md',
    neutral: 'bg-slate-700/95 text-white/90 shadow-md',
  }
  return (
    <div
      className="hidden sm:block relative rounded-2xl overflow-hidden select-none"
      style={{
        backgroundImage: 'url(/hero.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center 38%',   // más panorámico — menos zoom en detalle
        minHeight: '160px',
      }}
    >
      {/* Overlay — menos opaco a la derecha para mostrar más imagen */}
      <div
        className="absolute inset-0"
        style={{
          background: dark
            ? 'linear-gradient(105deg, rgba(2,6,23,0.96) 0%, rgba(2,6,23,0.78) 42%, rgba(2,6,23,0.12) 72%, transparent 100%)'
            : 'linear-gradient(105deg, rgba(59,69,89,0.96) 0%, rgba(59,69,89,0.75) 42%, rgba(59,69,89,0.12) 72%, transparent 100%)',
        }}
      />
      <div className="relative z-10 flex items-end justify-between gap-4 px-6 pt-5 pb-5">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <img src={brand.logoOnDark} alt={brand.name} className="h-4 w-auto object-contain opacity-60" style={brand.invertLogoOnDark ? { filter: 'invert(1)' } : undefined} />
            <span className="text-[8.5px] font-bold uppercase tracking-[0.24em] text-white/35">
              {`${brand.chromeLabel} Factory`}
            </span>
          </div>
          <p className="text-[1.1rem] sm:text-[1.2rem] font-bold text-white/90 tracking-tight leading-snug">
            {t('homeV2.tagline')}
          </p>
          <p className="text-[11px] text-white/40">{t('homeV2.taglineSub')}</p>
        </div>
        {/* Badge sólido — legible sobre fondo oscuro y sobre imagen */}
        <div className="shrink-0">
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full ${statusPill[overallStatus]}`}>
            <span className={`w-1.5 h-1.5 rounded-full bg-current shrink-0 ${overallStatus !== 'ok' ? 'animate-pulse' : ''}`} />
            {overallLabel}
          </span>
        </div>
      </div>
    </div>
  )
}

// ── 3. Mobile Alert Callout (sm:hidden) ───────────────────────────────────────

function MobileAlertCallout({ dark, totalAlertas, t }: { dark: boolean; totalAlertas: number; t: TFunc }) {
  if (totalAlertas === 0) return null
  const label = totalAlertas === 1
    ? t('homeV2.mobile.alertCalloutSingle')
    : t('homeV2.mobile.alertCallout', { count: totalAlertas })
  return (
    <div className={`sm:hidden flex items-center gap-3 px-3.5 py-2.5 rounded-xl border
      ${dark ? 'bg-rose-500/10 border-rose-500/25 text-rose-300' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
      <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse shrink-0" />
      <span className="text-[12px] font-semibold flex-1 min-w-0">{label}</span>
      <Link to="/produccion-diaria"
        className={`flex items-center gap-0.5 text-[11px] font-bold shrink-0 ${dark ? 'text-rose-400' : 'text-rose-600'}`}>
        {t('homeV2.alerts.goTo')} <IcoArrowSm />
      </Link>
    </div>
  )
}

// ── 4. KPI Card ───────────────────────────────────────────────────────────────

interface KpiV2Props {
  dark: boolean
  value: string
  label: string
  desc: string
  statusChip?: string
  icon: ReactNode
  accent: AccentColor
  pulseDot?: boolean
}

function KpiV2Card({ dark, value, label, desc, statusChip, icon, accent, pulseDot = false }: KpiV2Props) {
  type AccMap<T> = Record<AccentColor, T>
  const cardBg: AccMap<string> = {
    rose:    dark ? 'bg-rose-950/50 border-rose-500/25'       : 'bg-rose-50 border-rose-200',
    amber:   dark ? 'bg-amber-950/50 border-amber-500/25'     : 'bg-amber-50 border-amber-200',
    sky:     dark ? 'bg-sky-950/50 border-sky-500/25'         : 'bg-sky-50 border-sky-200',
    emerald: dark ? 'bg-emerald-950/50 border-emerald-500/25' : 'bg-emerald-50 border-emerald-200',
    neutral: dark ? 'bg-slate-900 border-white/8'             : 'bg-white border-brand-border shadow-sm',
  }
  const iconBg: AccMap<string> = {
    rose:    dark ? 'bg-rose-500/20 text-rose-400'       : 'bg-rose-100 text-rose-600',
    amber:   dark ? 'bg-amber-500/20 text-amber-400'     : 'bg-amber-100 text-amber-600',
    sky:     dark ? 'bg-sky-500/20 text-sky-400'         : 'bg-sky-100 text-sky-600',
    emerald: dark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600',
    neutral: dark ? 'bg-slate-700/80 text-slate-400'     : 'bg-slate-100 text-slate-500',
  }
  const valueCls: AccMap<string> = {
    rose:    dark ? 'text-rose-300'    : 'text-rose-700',
    amber:   dark ? 'text-amber-300'   : 'text-amber-700',
    sky:     dark ? 'text-sky-300'     : 'text-sky-700',
    emerald: dark ? 'text-emerald-300' : 'text-emerald-700',
    neutral: dark ? 'text-slate-100'   : 'text-slate-800',
  }
  const chipBg: AccMap<string> = {
    rose:    dark ? 'bg-rose-500/15 text-rose-300'       : 'bg-rose-100 text-rose-700',
    amber:   dark ? 'bg-amber-500/15 text-amber-300'     : 'bg-amber-100 text-amber-700',
    sky:     dark ? 'bg-sky-500/15 text-sky-300'         : 'bg-sky-100 text-sky-700',
    emerald: dark ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
    neutral: dark ? 'bg-slate-700 text-slate-400'        : 'bg-slate-100 text-slate-600',
  }
  return (
    <div className={`rounded-xl border p-4 sm:p-5 flex flex-col gap-3 ${cardBg[accent]}`}>
      <div className="flex items-start justify-between gap-2">
        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg[accent]}`}>
          {icon}
        </div>
        {statusChip && (
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full text-right max-w-[110px] leading-tight ${chipBg[accent]}`}>
            {statusChip}
          </span>
        )}
      </div>
      <div>
        <div className="flex items-center gap-1.5">
          <span className={`text-[2rem] sm:text-[2.4rem] font-bold tabular-nums leading-none ${valueCls[accent]}`}>
            {value}
          </span>
          {pulseDot && (
            <span className={`w-2 h-2 rounded-full bg-current animate-pulse shrink-0 ${valueCls[accent]}`} />
          )}
        </div>
        <div className={`text-[9px] sm:text-[9.5px] font-bold uppercase tracking-widest mt-1.5
          ${dark ? 'text-slate-300/60' : 'text-slate-600'}`}>
          {label}
        </div>
        <div className={`text-[10.5px] mt-0.5 leading-snug ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
          {desc}
        </div>
      </div>
    </div>
  )
}

// ── 5. Weekly Production Chart (SVG) ─────────────────────────────────────────
// FIX v2.3: h-full en todos los estados para igualar altura con la tabla de riesgo.
// SVG H aumentado a 220 para barras más altas. Área SVG usa flex-1 para crecer.

interface WeeklyChartProps {
  dark: boolean
  data: ProduccionSemanalPoint[]
  isLoading: boolean
  t: TFunc
}

function WeeklyProductionChart({ dark, data, isLoading, t }: WeeklyChartProps) {
  const svgRef   = useRef<SVGSVGElement>(null)
  const panelBg  = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-brand-border shadow-sm'
  const titleCls = dark ? 'text-slate-100' : 'text-slate-800'
  const subCls   = dark ? 'text-slate-500' : 'text-slate-400'
  const labelCol = dark ? '#64748b' : '#94a3b8'
  const axisCol  = dark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'
  const gridCol  = dark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'
  const planFill = dark ? '#1e293b' : '#e2e8f0'
  const prodFill = dark ? '#38bdf8' : '#0284c7'
  const capColor = dark ? '#fb923c' : '#ea580c'

  // Dimensiones SVG — H=220 para barras más altas
  const W = 520; const H = 220
  // PL da cabida a la etiqueta del eje Y con el número completo ("1.250.000").
  const PL = 64; const PR = 8; const PT = 14; const PB = 36
  const innerW = W - PL - PR
  const innerH = H - PT - PB

  const hasCapacity = data.some(d => d.capacidad != null && d.capacidad > 0)
  const maxQty = Math.max(
    ...data.map(d => Math.max(d.qtyProducida, d.qtyPlanificada, d.capacidad ?? 0)),
    1,
  )
  const slotW = innerW / Math.max(data.length, 1)
  const barW  = Math.max(Math.floor(slotW * 0.55), 4)

  if (isLoading) {
    return (
      <div className={`rounded-xl border p-4 flex flex-col gap-3 h-full ${panelBg}`}>
        <div className="flex flex-col gap-1 shrink-0">
          <div className={`h-4 w-44 rounded ${dark ? 'bg-white/5' : 'bg-slate-100'} animate-pulse`} />
          <div className={`h-3 w-28 rounded ${dark ? 'bg-white/4' : 'bg-slate-100'} animate-pulse mt-1`} />
        </div>
        <div className={`flex-1 min-h-[160px] rounded-lg ${dark ? 'bg-white/4' : 'bg-slate-50'} animate-pulse`} />
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className={`rounded-xl border p-4 flex flex-col gap-3 h-full ${panelBg}`}>
        <div className="shrink-0">
          <p className={`text-[13px] font-bold ${titleCls}`}>{t('homeV2.chart.title')}</p>
          <p className={`text-[11px] mt-0.5 ${subCls}`}>{t('homeV2.chart.subtitle')}</p>
        </div>
        <div className={`flex-1 flex items-center justify-center min-h-[160px] rounded-lg
          ${dark ? 'bg-white/3' : 'bg-slate-50'}`}>
          <p className={`text-[12px] ${subCls}`}>{t('homeV2.chart.empty')}</p>
        </div>
      </div>
    )
  }

  // Capacity polyline points
  const capPoints = hasCapacity
    ? data.map((point, i) => {
        const cx = PL + slotW * i + slotW / 2
        const y  = PT + innerH - innerH * ((point.capacidad ?? 0) / maxQty)
        return `${cx},${y}`
      }).join(' ')
    : ''

  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-3 h-full ${panelBg}`}>
      {/* Header row — shrink-0 para no comprimir el SVG */}
      <div className="shrink-0 flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className={`text-[13px] font-bold ${titleCls}`}>{t('homeV2.chart.title')}</p>
          <p className={`text-[11px] mt-0.5 ${subCls}`}>{t('homeV2.chart.subtitle')}</p>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-3 shrink-0 flex-wrap mt-0.5">
          <LegendDot color={planFill} label={t('homeV2.chart.qtyPlanificada')} subCls={subCls} />
          <LegendDot color={prodFill} label={t('homeV2.chart.qtyProducida')} subCls={subCls} />
          {hasCapacity && <LegendDash color={capColor} label={t('homeV2.chart.capacidad')} subCls={subCls} />}
        </div>
      </div>

      {/* SVG container — flex-1 para ocupar el espacio restante del panel */}
      <div className="flex-1 overflow-hidden min-h-[150px] flex flex-col justify-center">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          className="w-full h-auto"
          style={{ maxHeight: `${H}px` }}
          aria-label={t('homeV2.chart.title')}
        >
          {/* Y grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map(frac => {
            const y   = PT + innerH * (1 - frac)
            const val = Math.round(maxQty * frac)
            return (
              <g key={frac}>
                <line x1={PL} y1={y} x2={PL + innerW} y2={y}
                  stroke={gridCol} strokeWidth="1" />
                {frac > 0 && (
                  <text x={PL - 5} y={y + 3.5} textAnchor="end"
                    fontSize="8" fill={labelCol}>
                    {fmtUds(val)}
                  </text>
                )}
              </g>
            )
          })}

          {/* Bars */}
          {data.map((point, i) => {
            const cx    = PL + slotW * i + slotW / 2
            const half  = barW / 2
            const planH = innerH * (point.qtyPlanificada / maxQty)
            const prodH = innerH * (point.qtyProducida   / maxQty)
            const showLabel = i === 0 || i === data.length - 1 || i % 2 === 0
            return (
              <g key={point.fecha}>
                {point.qtyPlanificada > 0 && (
                  <rect x={cx - half} y={PT + innerH - planH}
                    width={barW} height={planH}
                    fill={planFill} rx="1.5" opacity={0.75} />
                )}
                {point.qtyProducida > 0 && (
                  <rect x={cx - half + 1} y={PT + innerH - prodH}
                    width={Math.max(barW - 2, 2)} height={prodH}
                    fill={prodFill} rx="1.5" opacity={0.9} />
                )}
                {showLabel && (
                  <text x={cx} y={H - 4} textAnchor="middle"
                    fontSize="7.5" fill={labelCol}>
                    {fmtDate(point.fecha)}
                  </text>
                )}
              </g>
            )
          })}

          {/* Capacity dashed line */}
          {hasCapacity && (
            <polyline
              points={capPoints}
              fill="none"
              stroke={capColor}
              strokeWidth="1.8"
              strokeDasharray="5,3"
              strokeLinecap="round"
              opacity={0.85}
            />
          )}

          {/* X-axis line */}
          <line x1={PL} y1={PT + innerH} x2={PL + innerW} y2={PT + innerH}
            stroke={axisCol} strokeWidth="1" />
        </svg>
      </div>

      {/* Footer link — shrink-0 */}
      <div className="shrink-0 flex justify-end">
        <Link to="/produccion-diaria"
          className={`flex items-center gap-0.5 text-[11px] font-semibold transition-colors
            ${dark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
        >
          {t('homeV2.chart.verTodas')} <IcoChevronRight />
        </Link>
      </div>
    </div>
  )
}

// Legend helpers
function LegendDot({ color, label, subCls }: { color: string; label: string; subCls: string }) {
  return (
    <span className="flex items-center gap-1 shrink-0">
      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: color }} />
      <span className={`text-[9.5px] ${subCls}`}>{label}</span>
    </span>
  )
}
function LegendDash({ color, label, subCls }: { color: string; label: string; subCls: string }) {
  return (
    <span className="flex items-center gap-1 shrink-0">
      <svg width="14" height="4" viewBox="0 0 14 4">
        <line x1="0" y1="2" x2="14" y2="2" stroke={color} strokeWidth="2" strokeDasharray="4,2" strokeLinecap="round" />
      </svg>
      <span className={`text-[9.5px] ${subCls}`}>{label}</span>
    </span>
  )
}

// ── 6. Production Risk Panel (scroll-bounded, h-full) ─────────────────────────

interface RiskTableProps {
  dark: boolean
  data: ProduccionRiesgoItem[]
  isLoading: boolean
  t: TFunc
  /** Total de OPs retrasadas (puede ser > data.length si sólo se devuelven TOP 10) */
  totalRetrasadas?: number
}

function ProductionRiskTable({ dark, data, isLoading, t, totalRetrasadas }: RiskTableProps) {
  const fmtArt = useFmtArticulo()
  const panelBg  = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-brand-border shadow-sm'
  const titleCls = dark ? 'text-slate-100' : 'text-slate-800'
  const subCls   = dark ? 'text-slate-500' : 'text-slate-400'
  const divider  = dark ? 'border-white/6' : 'border-slate-100'
  const rowHover = dark ? 'hover:bg-white/3' : 'hover:bg-slate-50/80'
  const cellMain = dark ? 'text-slate-200' : 'text-slate-700'
  const cellSub  = dark ? 'text-slate-500' : 'text-slate-400'

  const estadoLabel: Record<number, string> = {
    3: t('homeV2.riskTable.estado3'),
    4: t('homeV2.riskTable.estado4'),
  }
  const estadoColor: Record<number, string> = {
    3: dark ? 'bg-sky-500/15 text-sky-300'         : 'bg-sky-50 text-sky-700',
    4: dark ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700',
  }

  if (isLoading) {
    return (
      <div className={`rounded-xl border flex flex-col h-full ${panelBg}`}>
        <div className={`px-4 pt-4 pb-3 border-b ${divider} shrink-0`}>
          <div className={`h-4 w-44 rounded ${dark ? 'bg-white/5' : 'bg-slate-100'} animate-pulse`} />
        </div>
        <div className="p-3 flex flex-col gap-2 flex-1">
          {[0,1,2,3].map(i => (
            <div key={i} className={`h-10 rounded-lg ${dark ? 'bg-white/4' : 'bg-slate-50'} animate-pulse`}
              style={{ opacity: 1 - i * 0.15 }} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={`rounded-xl border flex flex-col h-full ${panelBg}`}>

      {/* Header — sticky, shrink-0 */}
      <div className={`flex items-center justify-between gap-2 px-4 pt-3.5 pb-3 border-b ${divider} shrink-0`}>
        <div>
          <p className={`text-[13px] font-bold ${titleCls}`}>{t('homeV2.riskTable.title')}</p>
          <p className={`text-[10.5px] mt-0.5 ${subCls}`}>
            {(totalRetrasadas != null && totalRetrasadas > data.length && data.length > 0)
              ? t('homeV2.riskTable.subtitleCount', { shown: data.length, total: totalRetrasadas })
              : t('homeV2.riskTable.subtitle')}
          </p>
        </div>
        <Link to="/produccion-diaria"
          className={`flex items-center gap-0.5 text-[11px] font-semibold shrink-0
            ${dark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}
            transition-colors`}
        >
          {t('homeV2.riskTable.goTo')} <IcoChevronRight />
        </Link>
      </div>

      {data.length === 0 ? (
        <div className={`flex items-center gap-3 px-4 py-4 flex-1
          ${dark ? 'bg-emerald-500/5' : 'bg-emerald-50/60'} rounded-b-xl`}>
          <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
          <span className={`text-[12px] font-medium ${dark ? 'text-emerald-400' : 'text-emerald-700'}`}>
            {t('homeV2.riskTable.empty')}
          </span>
        </div>
      ) : (
        <>
          {/* Desktop: scrollable table — flex-1 con scroll interno */}
          <div className={`hidden sm:block overflow-y-auto flex-1`}>
            <table className="w-full text-[11.5px] border-collapse">
              <thead className={`sticky top-0 z-10 ${dark ? 'bg-slate-900' : 'bg-white'}`}>
                <tr className={`border-b ${divider}`}>
                  <th className={`text-left px-4 py-2 font-semibold uppercase tracking-wide text-[9px] ${cellSub} whitespace-nowrap`}>
                    {t('homeV2.riskTable.numOp')}
                  </th>
                  <th className={`text-left px-2 py-2 font-semibold uppercase tracking-wide text-[9px] ${cellSub}`}>
                    {t('homeV2.riskTable.articulo')}
                  </th>
                  <th className={`text-left px-2 py-2 font-semibold uppercase tracking-wide text-[9px] ${cellSub} whitespace-nowrap`}>
                    {t('homeV2.riskTable.fechaEntrega')}
                  </th>
                  <th className={`text-right px-4 py-2 font-semibold uppercase tracking-wide text-[9px] ${cellSub}`}>
                    {t('homeV2.riskTable.estado3') ? 'Estado' : 'Estado'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((op) => {
                  const overdue = daysOverdue(op.fechaEntregaOp)
                  return (
                    <tr key={`${op.dataareaid}-${op.numOp}`}
                      className={`border-b last:border-0 transition-colors ${divider} ${rowHover}`}>
                      <td className={`px-4 py-2.5 font-mono text-[10.5px] font-semibold whitespace-nowrap ${cellMain}`}>
                        {op.numOp}
                      </td>
                      <td className="px-2 py-2.5 max-w-[130px]">
                        <div className={`font-medium truncate ${cellMain}`}>{fmtArt(op.articulo)}</div>
                        <div className={`text-[9.5px] truncate ${cellSub}`}>{op.descripcion}</div>
                      </td>
                      <td className="px-2 py-2.5 whitespace-nowrap">
                        <div className={`font-semibold text-[11px] ${dark ? 'text-rose-300' : 'text-rose-700'}`}>
                          {fmtDate(op.fechaEntregaOp)}
                        </div>
                        {overdue > 0 && (
                          <div className={`text-[9px] ${cellSub}`}>+{overdue} {t('homeV2.riskTable.daysDelay')}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <span className={`text-[9.5px] font-semibold px-2 py-0.5 rounded-full
                          ${estadoColor[op.estadoOp] ?? (dark ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-600')}`}>
                          {estadoLabel[op.estadoOp] ?? `E${op.estadoOp}`}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list max 5 items */}
          <div className="sm:hidden flex flex-col flex-1 overflow-hidden">
            {data.slice(0, 5).map((op, idx) => {
              const overdue = daysOverdue(op.fechaEntregaOp)
              const isLast  = idx === Math.min(data.length, 5) - 1
              return (
                <div key={`${op.dataareaid}-${op.numOp}`}
                  className={`px-4 py-2.5 flex items-start justify-between gap-3
                    ${!isLast ? `border-b ${divider}` : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`font-mono text-[10.5px] font-bold ${cellMain}`}>{op.numOp}</span>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full
                        ${estadoColor[op.estadoOp] ?? (dark ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-600')}`}>
                        {estadoLabel[op.estadoOp] ?? `E${op.estadoOp}`}
                      </span>
                    </div>
                    <div className={`text-[12px] font-medium truncate ${cellMain}`}>{fmtArt(op.articulo)}</div>
                    <div className={`text-[10px] truncate ${cellSub}`}>{op.descripcion}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-[11px] font-semibold ${dark ? 'text-rose-300' : 'text-rose-700'}`}>
                      {fmtDate(op.fechaEntregaOp)}
                    </div>
                    {overdue > 0 && <div className={`text-[9.5px] ${cellSub}`}>+{overdue}d</div>}
                  </div>
                </div>
              )
            })}
            {data.length >= 5 && (
              <div className={`border-t ${divider} px-4 py-2.5`}>
                <Link to="/produccion-diaria"
                  className={`flex items-center justify-center gap-1 text-[11.5px] font-semibold
                    ${dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}
                    transition-colors`}
                >
                  {t('homeV2.riskTable.goTo')} <IcoArrowSm />
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ── 7. Alert Row ──────────────────────────────────────────────────────────────

function AlertRowV2({ dark, dotClass, label, count, to, severity, goToLabel }: {
  dark: boolean; dotClass: string; label: string; count: number
  to: string; severity: 'danger' | 'warn'; goToLabel: string
}) {
  const rowBg  = dark ? 'bg-white/[0.03] hover:bg-white/[0.06] border-white/6' : 'bg-slate-50 hover:bg-slate-100/80 border-slate-100'
  const lbl    = dark ? 'text-slate-200' : 'text-slate-700'
  const chip   = severity === 'danger'
    ? (dark ? 'bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/20' : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200')
    : (dark ? 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/20' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200')
  const lnk    = dark ? 'text-slate-500 group-hover:text-slate-300' : 'text-slate-400 group-hover:text-slate-600'
  return (
    <Link to={to}
      className={`group flex items-center gap-3 px-3.5 py-2.5 rounded-lg border transition-colors cursor-pointer ${rowBg}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 ${dotClass}`} />
      <span className={`text-[12px] font-medium flex-1 min-w-0 truncate ${lbl}`}>{label}</span>
      <span className={`text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full shrink-0 ${chip}`}>{fmtN(count)}</span>
      <span className={`flex items-center gap-0.5 text-[11px] font-medium shrink-0 transition-colors ${lnk}`}>
        {goToLabel}<IcoArrowSm />
      </span>
    </Link>
  )
}

// ── 8. Quick Access Grid ──────────────────────────────────────────────────────
// FIX v2.3:
//   Desktop: grid 5-col con subtítulo/contador dentro de cada card.
//   Mobile:  fila horizontal con scroll lateral (snap-x), cards compactas.
//   El subtítulo está dentro de la card — no flotando fuera.

interface QuickItem {
  to: string
  label: string
  subtitle?: string        // contador o descripción breve — dentro de la card
  icon: ReactNode
  iconBg: string
  iconColor: string
}

function QuickAccessGrid({ dark, items }: { dark: boolean; items: QuickItem[] }) {
  const card = dark
    ? 'bg-slate-900 border-white/8 hover:border-white/18 hover:bg-slate-800/60'
    : 'bg-white border-brand-border shadow-sm hover:border-slate-300 hover:shadow-md'
  const lbl = dark ? 'text-slate-300' : 'text-slate-700'
  const sub = dark ? 'text-slate-500' : 'text-slate-400'

  return (
    <>
      {/* ── Mobile: fila horizontal con scroll suave ── */}
      <div className="flex sm:hidden overflow-x-auto gap-2 pb-1 snap-x snap-mandatory">
        {items.map(item => (
          <Link
            key={`mob-${item.to}`}
            to={item.to}
            className={`group snap-start shrink-0 w-[96px] flex flex-col items-center
              justify-start gap-1.5 px-2 pt-3 pb-2.5
              rounded-xl border transition-all cursor-pointer ${card}`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center
              ${item.iconBg} ${item.iconColor}
              group-hover:scale-105 transition-transform duration-150`}>
              {item.icon}
            </div>
            <span className={`text-[10px] font-semibold text-center leading-tight ${lbl}`}>
              {item.label}
            </span>
            {item.subtitle && (
              <span className={`text-[9px] text-center leading-none ${sub}`}>
                {item.subtitle}
              </span>
            )}
          </Link>
        ))}
      </div>

      {/* ── Escritorio: una columna por tarjeta, hasta cinco ──
          Fijar cinco columnas dejaba dos huecos muertos cuando son tres
          tarjetas, y con tres el símbolo y el nombre respiran mejor. */}
      <div className={`hidden sm:grid gap-2 sm:gap-3 ${
        items.length <= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-5'
      }`}>
        {items.map((item) => (
          <Link
            key={`dsk-${item.to}`}
            to={item.to}
            className={`group flex flex-col items-center justify-center gap-2 px-2 py-4
              rounded-xl border transition-all cursor-pointer ${card}`}
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center
              ${item.iconBg} ${item.iconColor}
              group-hover:scale-105 transition-transform duration-150`}>
              {item.icon}
            </div>
            <span className={`text-[11.5px] font-semibold text-center leading-tight px-1 ${lbl}`}>
              {item.label}
            </span>
            {item.subtitle && (
              <span className={`text-[9.5px] text-center leading-tight ${sub}`}>
                {item.subtitle}
              </span>
            )}
          </Link>
        ))}
      </div>
    </>
  )
}

// ── Loading Skeleton ──────────────────────────────────────────────────────────

function LoadingV2({ dark }: { dark: boolean }) {
  const pulse = dark ? 'bg-white/5 animate-pulse rounded-xl' : 'bg-slate-200/70 animate-pulse rounded-xl'
  const card  = dark
    ? 'bg-slate-900 border border-white/8 rounded-xl p-4'
    : 'bg-white border border-brand-border rounded-xl p-4 shadow-sm'

  return (
    <div className="space-y-4">
      <div className={`h-11 ${pulse}`} />
      <div className={`h-[160px] hidden sm:block ${pulse}`} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[0,1,2,3].map(i => <div key={i} className={`h-[128px] ${pulse}`} style={{ opacity: 1 - i * 0.06 }} />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className={`lg:col-span-3 lg:h-[380px] h-[260px] ${card}`} />
        <div className={`lg:col-span-2 lg:h-[380px] h-[200px] ${card}`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className={`lg:col-span-3 h-[192px] ${card}`} />
        <div className={`lg:col-span-2 h-[192px] ${card}`} />
      </div>
    </div>
  )
}

// ── Error State ───────────────────────────────────────────────────────────────

function ErrorV2({ dark, onRetry, t }: { dark: boolean; onRetry: () => void; t: TFunc }) {
  const btn = dark
    ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 shadow-sm'
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <svg className={`w-10 h-10 opacity-25 ${dark ? 'text-slate-400' : 'text-slate-500'}`}
        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
      </svg>
      <p className={`text-sm font-semibold ${dark ? 'text-slate-200' : 'text-slate-800'}`}>{t('homeV2.error')}</p>
      <button onClick={onRetry}
        className={`text-xs font-medium px-4 py-2 rounded-lg border transition-colors cursor-pointer ${btn}`}>
        {t('homeV2.errorRetry')}
      </button>
    </div>
  )
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function DashboardV2Page() {
  const { t }    = useTranslation()
  const { dark } = useAppStore()
  const brand    = useBrand()

  const { accounts } = useMsal()
  const firstName = accounts[0]?.name?.trim().split(/\s+/)[0] ?? ''

  const { data, isLoading, isError, refetch, dataUpdatedAt, isFetching } = useDashboardData()
  // Arriba con los demás hooks a propósito: más abajo hay salidas tempranas por
  // carga y por error, y un hook detrás de un `return` se llama en unos renders
  // y no en otros.
  const verReports = useHasReportsAccess()
  // Quien sólo compra y vende: el inicio se recompone. Ver `useSinProduccion`.
  const sinProduccion = useSinProduccion()

  // Las dos consultas de producción no se piden en modo comercial: esos bloques
  // no se pintan, y traerlos sería pagar dos viajes para tirarlos.
  const { data: riskData, isLoading: riskLoading } = useQuery<ProduccionRiesgoItem[]>({
    queryKey: ['dashboard', 'producciones-riesgo'],
    queryFn:  async () => (await dashboardApi.getProduccionesRiesgo()).data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !sinProduccion,
  })

  const { data: chartData, isLoading: chartLoading } = useQuery<ProduccionSemanalPoint[]>({
    queryKey: ['dashboard', 'produccion-semanal'],
    queryFn:  async () => (await dashboardApi.getProduccionSemanal()).data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !sinProduccion,
  })

  const updatedAgo = (() => {
    if (!dataUpdatedAt) return ''
    const s = Math.floor((Date.now() - dataUpdatedAt) / 1000)
    if (s < 5)  return t('homeV2.updatedAgo', { value: '< 5s' })
    if (s < 60) return t('homeV2.updatedAgo', { value: `${s}s` })
    return t('homeV2.updatedAgo', { value: `${Math.floor(s / 60)} min` })
  })()

  if (isLoading || !data) return <LoadingV2 dark={dark} />
  if (isError)            return <ErrorV2 dark={dark} onRetry={refetch} t={t as TFunc} />

  const { compras, ventas, produccion, inventario } = data

  // ── Derived values ────────────────────────────────────────────────────────

  // Para quien no fabrica, las OPs retrasadas no son suyas: contarlas pondría
  // el semáforo en rojo por algo que no puede tocar.
  const totalAlertas = sinProduccion
    ? (ventas.pedidosRetrasados ?? 0)
    : (produccion.opsRetrasadas ?? 0) + (ventas.pedidosRetrasados ?? 0)

  const prodRiesgoAccent: AccentColor = (produccion.produccionesEnRiesgo ?? 0) === 0 ? 'emerald' : 'amber'
  const matRiesgoAccent: AccentColor  = (produccion.materialesEnRiesgo   ?? 0) === 0 ? 'emerald' : 'amber'
  const otsFechaAccent:  AccentColor  =
    produccion.opsRetrasadas === 0 ? 'emerald' : produccion.opsRetrasadas > 10 ? 'rose' : 'amber'
  const coberturaAccent: AccentColor  =
    (produccion.coberturaMaterial ?? 100) >= 95 ? 'emerald'
    : (produccion.coberturaMaterial ?? 100) >= 80 ? 'amber' : 'rose'

  const ventasRetrasadasAccent: AccentColor =
    (ventas.pedidosRetrasados ?? 0) === 0 ? 'emerald'
    : (ventas.pedidosRetrasados ?? 0) > 10 ? 'rose' : 'amber'
  const comprasRiesgoAccent: AccentColor =
    (compras.posibleRetraso ?? 0) === 0 ? 'emerald' : 'amber'

  const overallStatus: StatusKind =
    totalAlertas === 0 ? 'ok' : totalAlertas > 15 ? 'danger' : 'warn'
  const overallLabel = totalAlertas === 0
    ? t('homeV2.status.ok')
    : t('homeV2.status.withAlerts', { count: totalAlertas })

  const panelBg = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-brand-border shadow-sm'
  const divider = dark ? 'border-white/8' : 'border-slate-100'
  const txtMain = dark ? 'text-slate-100' : 'text-slate-800'
  const txtSub  = dark ? 'text-slate-400' : 'text-slate-500'

  // Acceso rápido a los tres reports de cliente. Solo para quien tiene acceso a
  // la empresa avp; el resto sigue viendo el acceso rápido a las páginas, para no
  // dejar el bloque vacío ni ofrecer un destino que devolvería un 403.
  //
  // El subtítulo es una descripción fija, no un recuento en vivo: contarlo obliga
  // a traerse los tres reports al abrir la home, y son las vistas de Fabric que
  // tardan entre 6 y 50 s. La home no puede pagar eso por un número.
  const reportItems: QuickItem[] = [
    {
      to: '/reports/raw-materials',
      label:    t('reports.rawMaterials.title'),
      subtitle: t('homeV2.quickActions.reportRawMaterialsSub'),
      icon: <IcoDrum24 />,
      iconBg:    dark ? 'bg-[#becae8]/15' : 'bg-[#becae8]/40',
      iconColor: dark ? 'text-[#becae8]'  : 'text-[#4a6ba8]',
    },
    {
      to: '/reports/fg',
      label:    t('reports.fg.title'),
      subtitle: t('homeV2.quickActions.reportFgSub'),
      icon: <IcoBottle24 />,
      iconBg:    dark ? 'bg-[#c5e4e0]/15' : 'bg-[#c5e4e0]/50',
      iconColor: dark ? 'text-[#c5e4e0]'  : 'text-[#3a8e88]',
    },
    {
      to: '/reports/fg-to-rm',
      label:    t('reports.fgToRm.title'),
      subtitle: t('homeV2.quickActions.reportFgToRmSub'),
      icon: <IcoNet24 />,
      iconBg:    dark ? 'bg-violet-500/15' : 'bg-violet-100',
      iconColor: dark ? 'text-violet-400'  : 'text-violet-600',
    },
  ]

  // Quick access — subtítulos con datos reales, dentro de cada card.
  // En modo comercial se caen Producción diaria y el Gantt, y entra Cargas de
  // salida, que es donde acaba un pedido de venta.
  const quickItems: QuickItem[] = [
    {
      to: '/pedidos-compra',
      label:    t('homeV2.quickActions.compras'),
      subtitle: t('homeV2.quickActions.comprasSub', { count: fmtN(compras.pedidosAbiertos) }),
      icon: <IcoCart24 />,
      iconBg:    dark ? 'bg-[#becae8]/15' : 'bg-[#becae8]/40',
      iconColor: dark ? 'text-[#becae8]'  : 'text-[#4a6ba8]',
    },
    {
      to: '/pedidos-ventas',
      label:    t('homeV2.quickActions.ventas'),
      subtitle: t('homeV2.quickActions.ventasSub', { count: fmtN(ventas.pedidosAbiertos) }),
      icon: <IcoBag24 />,
      iconBg:    dark ? 'bg-[#c5e4e0]/15' : 'bg-[#c5e4e0]/50',
      iconColor: dark ? 'text-[#c5e4e0]'  : 'text-[#3a8e88]',
    },
    ...(sinProduccion ? [] : [{
      to: '/produccion-diaria',
      label:    t('homeV2.quickActions.produccion'),
      subtitle: (produccion.produccionHoy ?? 0) > 0
        ? t('homeV2.quickActions.produccionHoy', { qty: fmtUds(produccion.produccionHoy) })
        : t('homeV2.quickActions.produccionSub', { count: fmtN(produccion.ordenesActivas) }),
      icon: <IcoBars24 />,
      iconBg:    dark ? 'bg-sky-500/15' : 'bg-sky-100',
      iconColor: dark ? 'text-sky-400'  : 'text-sky-600',
    }]),
    {
      to: '/inventario',
      label:    t('homeV2.quickActions.inventario'),
      subtitle: t('homeV2.quickActions.inventarioSub', { count: fmtN(inventario.totalReferencias) }),
      icon: <IcoBox24 />,
      iconBg:    dark ? 'bg-emerald-500/15' : 'bg-emerald-100',
      iconColor: dark ? 'text-emerald-400'  : 'text-emerald-600',
    },
    ...(sinProduccion ? [{
      to: '/cargas-salida',
      label:    t('homeV2.quickActions.cargas'),
      subtitle: t('homeV2.quickActions.cargasSub'),
      icon: <IcoTruck24 />,
      iconBg:    dark ? 'bg-violet-500/15' : 'bg-violet-100',
      iconColor: dark ? 'text-violet-400'  : 'text-violet-600',
    }] : [{
      to: '/gantt',
      label:    t('homeV2.quickActions.avanzado'),
      subtitle: t('homeV2.quickActions.avanzadoSub'),
      icon: <IcoCal24 />,
      iconBg:    dark ? 'bg-violet-500/15' : 'bg-violet-100',
      iconColor: dark ? 'text-violet-400'  : 'text-violet-600',
    }]),
  ]

  return (
    <div className="space-y-4 pt-1 md:pt-0">

      {/* ══════════════════════════════════════════════════════════════════════
          1. GREETING HEADER
          ══════════════════════════════════════════════════════════════════════ */}
      <GreetingHeader
        dark={dark} firstName={firstName} updatedAgo={updatedAgo}
        isFetching={isFetching} onRefresh={refetch} t={t as TFunc}
      />

      {/* ══════════════════════════════════════════════════════════════════════
          2. HERO BANNER (hidden on mobile) — imagen panorámica + badge sólido
          ══════════════════════════════════════════════════════════════════════ */}
      <HeroBanner dark={dark} overallStatus={overallStatus} overallLabel={overallLabel} t={t as TFunc} brand={brand} />

      {/* ══════════════════════════════════════════════════════════════════════
          3. MOBILE ALERT CALLOUT
          ══════════════════════════════════════════════════════════════════════ */}
      <MobileAlertCallout dark={dark} totalAlertas={totalAlertas} t={t as TFunc} />

      {/* ══════════════════════════════════════════════════════════════════════
          4. KPI STRIP — 2-col mobile, 4-col desktop
          ══════════════════════════════════════════════════════════════════════ */}
      {sinProduccion ? (
      /* Los cinco de quien compra y vende. Salen todos del mismo /dashboard que
         ya se pide: ningún viaje nuevo. */
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">

        <KpiV2Card
          dark={dark}
          value={fmtN(ventas.pedidosRetrasados ?? 0)}
          label={t('homeV2.kpi.ventasRetrasadas')}
          desc={t((ventas.pedidosRetrasados ?? 0) === 0 ? 'homeV2.kpi.ventasRetrasadasOk' : 'homeV2.kpi.ventasRetrasadasDesc')}
          icon={<IcoAlert />}
          accent={ventasRetrasadasAccent}
          pulseDot={(ventas.pedidosRetrasados ?? 0) > 0}
        />

        <KpiV2Card
          dark={dark}
          value={fmtN(ventas.entregaProxima7d ?? 0)}
          label={t('homeV2.kpi.upcomingDeliveries')}
          desc={t((ventas.entregaProxima7d ?? 0) === 0 ? 'homeV2.kpi.upcomingDeliveriesNone' : 'homeV2.kpi.upcomingDeliveriesDesc')}
          icon={<IcoClock />}
          accent={(ventas.entregaProxima7d ?? 0) === 0 ? 'emerald' : 'sky'}
        />

        <KpiV2Card
          dark={dark}
          /* Sin moneda no se pinta el importe: la suma mezcla monedas en origen
             —medido: EUR, USD, AED y GBP— y un total con símbolo inventado es
             una cifra creíble y falsa. Entonces se enseñan las unidades, que no
             dependen de ninguna moneda. */
          value={ventas.moneda
            ? `${fmtN(ventas.importePendiente)} ${ventas.moneda}`
            : fmtUds(ventas.qtyPendienteTotal)}
          label={t(ventas.moneda ? 'homeV2.kpi.importePendiente' : 'homeV2.kpi.unidadesPendientes')}
          desc={t(ventas.moneda ? 'homeV2.kpi.importePendienteDesc' : 'homeV2.kpi.unidadesPendientesDesc')}
          icon={<IcoBag24 />}
          accent="neutral"
        />

        <KpiV2Card
          dark={dark}
          value={fmtN(compras.posibleRetraso ?? 0)}
          label={t('homeV2.kpi.purchaseRisk')}
          desc={t((compras.posibleRetraso ?? 0) === 0 ? 'homeV2.kpi.purchaseRiskStable' : 'homeV2.kpi.purchaseRiskDesc')}
          statusChip={(compras.pedidosAbiertos ?? 0) > 0
            ? t('homeV2.kpi.comprasAbiertasChip', { count: fmtN(compras.pedidosAbiertos) })
            : undefined}
          icon={<IcoCart24 />}
          accent={comprasRiesgoAccent}
          pulseDot={(compras.posibleRetraso ?? 0) > 0}
        />

        <KpiV2Card
          dark={dark}
          value={fmtN(inventario.articulosConStock ?? 0)}
          label={t('homeV2.kpi.stock')}
          desc={t('homeV2.kpi.stockDesc')}
          statusChip={(inventario.totalReferencias ?? 0) > 0
            ? t('homeV2.kpi.stockChip', { count: fmtN(inventario.totalReferencias) })
            : undefined}
          icon={<IcoBox />}
          accent="neutral"
        />

      </div>
      ) : (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

        <KpiV2Card
          dark={dark}
          value={fmtN(produccion.produccionesEnRiesgo ?? 0)}
          label={t('homeV2.kpi.prodRiesgo')}
          desc={t((produccion.produccionesEnRiesgo ?? 0) === 0 ? 'homeV2.kpi.prodRiesgoOk' : 'homeV2.kpi.prodRiesgoDesc')}
          icon={<IcoAlert />}
          accent={prodRiesgoAccent}
          pulseDot={(produccion.produccionesEnRiesgo ?? 0) > 0}
        />

        <KpiV2Card
          dark={dark}
          value={fmtN(produccion.materialesEnRiesgo ?? 0)}
          label={t('homeV2.kpi.matRiesgo')}
          desc={t((produccion.materialesEnRiesgo ?? 0) === 0 ? 'homeV2.kpi.matRiesgoOk' : 'homeV2.kpi.matRiesgoDesc')}
          icon={<IcoBox />}
          accent={matRiesgoAccent}
          pulseDot={(produccion.materialesEnRiesgo ?? 0) > 0}
        />

        <KpiV2Card
          dark={dark}
          value={fmtN(produccion.opsRetrasadas)}
          label={t('homeV2.kpi.otsFecha')}
          desc={t(produccion.opsRetrasadas === 0 ? 'homeV2.kpi.otsFechaOk' : 'homeV2.kpi.otsFechaDesc')}
          statusChip={produccion.opsRetrasadas > 0
            ? `${fmtN(produccion.ordenesActivas)} activas`
            : undefined}
          icon={<IcoClock />}
          accent={otsFechaAccent}
          pulseDot={produccion.opsRetrasadas > 0}
        />

        <KpiV2Card
          dark={dark}
          value={fmtPct(produccion.coberturaMaterial ?? 100)}
          label={t('homeV2.kpi.cobertura')}
          desc={t((produccion.coberturaMaterial ?? 100) >= 95 ? 'homeV2.kpi.coberturaOk' : 'homeV2.kpi.coberturaDesc')}
          statusChip={(produccion.materialesEnRiesgo ?? 0) > 0
            ? `${fmtN(produccion.materialesEnRiesgo)} mat.`
            : undefined}
          icon={<IcoShield />}
          accent={coberturaAccent}
        />

      </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          5. ANALYTICAL BLOCK — gráfico (3col) + tabla riesgo (2col)
          FIX v2.4: altura explícita en las celdas (lg:h-[380px]).
          El diseño fija la altura — el contenido NO la determina.
          Ambos componentes usan h-full para rellenar la celda.
          La tabla de riesgo hace scroll interno (flex-1 overflow-y-auto).
          En mobile: height auto (sin lg: prefix) → apilado natural.
          ══════════════════════════════════════════════════════════════════════ */}
      {!sinProduccion && (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        <div className="lg:col-span-3 lg:h-[380px]">
          <WeeklyProductionChart
            dark={dark} data={chartData ?? []}
            isLoading={chartLoading} t={t as TFunc}
          />
        </div>

        <div className="lg:col-span-2 lg:h-[380px]">
          <ProductionRiskTable
            dark={dark} data={riskData ?? []}
            isLoading={riskLoading} t={t as TFunc}
            totalRetrasadas={produccion.opsRetrasadas}
          />
        </div>

      </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          6. ALERTS + QUICK ACCESS — 3:2 split desktop, stacked mobile
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Alert panel */}
        <div className={`lg:col-span-3 rounded-xl border p-4 flex flex-col gap-3 ${panelBg}`}>

          <div className={`flex items-center justify-between pb-2.5 border-b ${divider}`}>
            <div>
              <p className={`text-[13px] font-bold ${txtMain}`}>{t('homeV2.alerts.title')}</p>
              <p className={`text-[11px] mt-0.5 ${txtSub}`}>{t('homeV2.alerts.subtitle')}</p>
            </div>
            {totalAlertas > 0 ? (
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full
                ${dark ? 'bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/20' : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                {fmtN(totalAlertas)}
              </span>
            ) : (
              <span className={`text-[10.5px] font-semibold px-2.5 py-1 rounded-full
                ${dark ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/20' : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'}`}>
                {t('homeV2.status.ok')}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {totalAlertas === 0 && compras.posibleRetraso === 0 && ventas.entregaProxima7d === 0 ? (
              <div className={`flex items-center gap-3 px-3.5 py-3 rounded-lg border
                ${dark ? 'bg-emerald-500/8 border-emerald-500/20' : 'bg-emerald-50 border-emerald-100'}`}>
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                <span className={`text-[12px] font-medium ${dark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  {t('homeV2.alerts.none')}
                </span>
              </div>
            ) : (
              <>
                {compras.posibleRetraso > 0 && (
                  <AlertRowV2
                    dark={dark} dotClass="bg-amber-500"
                    label={t('homeV2.alerts.comprasAntiguas')} count={compras.posibleRetraso}
                    to="/pedidos-compra" severity="warn" goToLabel={t('homeV2.alerts.goTo')} />
                )}
                {ventas.pedidosRetrasados > 0 && (
                  <AlertRowV2
                    dark={dark} dotClass="bg-rose-500"
                    label={t('homeV2.alerts.ventasRetrasadas')} count={ventas.pedidosRetrasados}
                    to="/pedidos-ventas" severity="danger" goToLabel={t('homeV2.alerts.goTo')} />
                )}
                {/* La avería de una OP no es de quien sólo compra y vende, y
                    además le llevaría a una pantalla que no puede abrir. */}
                {!sinProduccion && produccion.opsRetrasadas > 0 && (
                  <AlertRowV2
                    dark={dark} dotClass="bg-rose-400"
                    label={t('homeV2.alerts.opsRetrasadas')} count={produccion.opsRetrasadas}
                    to="/produccion-diaria" severity="danger" goToLabel={t('homeV2.alerts.goTo')} />
                )}
                {ventas.entregaProxima7d > 0 && (
                  <AlertRowV2
                    dark={dark} dotClass="bg-amber-400"
                    label={t('homeV2.alerts.entregasProximas')} count={ventas.entregaProxima7d}
                    to="/pedidos-ventas" severity="warn" goToLabel={t('homeV2.alerts.goTo')} />
                )}
              </>
            )}
          </div>
        </div>

        {/* Quick access — cards autocontenidas con subtítulo/contador */}
        <div className={`lg:col-span-2 rounded-xl border p-4 flex flex-col gap-3 ${panelBg}`}>
          <div className={`pb-2.5 border-b ${divider}`}>
            {/* El rótulo dice qué hay dentro, y dentro no siempre hay lo mismo.
                Con acceso son los reports, y se reutiliza la etiqueta del menú
                lateral para que se llamen igual en los dos sitios; sin acceso son
                los atajos a las páginas y sigue siendo «Acceso rápido». */}
            <p className={`text-[13px] font-bold ${txtMain}`}>
              {verReports ? t('nav.reports') : t('homeV2.quickActions.title')}
            </p>
          </div>
          <QuickAccessGrid dark={dark} items={verReports ? reportItems : quickItems} />
        </div>

      </div>

    </div>
  )
}
